import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { fivemLogAPI } from '../services/api';
import FiveMLogTable, { TABLE_META } from '../components/FiveMLogTable';
import RealtimeBadge from '../components/RealtimeBadge';
import ModShiftSummary from '../components/ModShiftSummary';
import { FiSearch, FiRefreshCw } from 'react-icons/fi';

const getDefaultDates = () => ({ startDate: '', endDate: '' });

const GroupLogs = () => {
    const { groupKey } = useParams();
    const [logs, setLogs]       = useState([]);
    const [loading, setLoading] = useState(true);
    const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, pages: 0 });
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch]   = useState('');
    const [dates, setDates]     = useState(getDefaultDates());

    const meta = TABLE_META[groupKey] || { label: groupKey, icon: '📋', color: '#6366f1', cls: '' };

    const fetchLogs = useCallback(async (page = 1) => {
        try {
            setLoading(true);
            const res = await fivemLogAPI.getLogs({
                table:     groupKey,
                page,
                limit:     50,
                search,
                startDate: dates.startDate || undefined,
                endDate:   dates.endDate   || undefined,
            });
            setLogs(res.data.logs || []);
            setPagination(res.data.pagination || { page: 1, limit: 50, total: 0, pages: 0 });
        } catch (err) {
            console.error(err);
            toast.error('Không thể tải logs');
        } finally {
            setLoading(false);
        }
    }, [groupKey, search, dates]);

    useEffect(() => { fetchLogs(1); }, [fetchLogs]);

    const handleSearch = (e) => {
        e.preventDefault();
        setSearch(searchInput);
    };

    const handleToday = () => {
        const d = new Date(); d.setHours(0, 0, 0, 0);
        setDates({ startDate: d.toISOString().slice(0, 16), endDate: new Date().toISOString().slice(0, 16) });
    };

    const handle7days = () => {
        const end = new Date();
        const start = new Date(end.getTime() - 7 * 24 * 60 * 60 * 1000);
        setDates({ startDate: start.toISOString().slice(0, 16), endDate: end.toISOString().slice(0, 16) });
    };

    return (
        <div className="logs-page">
            <div className="logs-header">
                <div className="logs-title-row">
                    <span className="group-icon">{meta.icon}</span>
                    <h1 style={{ color: meta.color }}>{meta.label}</h1>
                    <RealtimeBadge />
                </div>
                <div className="logs-header-right">
                    <p className="logs-subtitle" style={{ color: meta.color }}>
                        {pagination.total?.toLocaleString()} logs · FiveM Realtime
                    </p>
                    <button className="logs-refresh-btn" onClick={() => fetchLogs(1)} disabled={loading}>
                        <FiRefreshCw className={loading ? 'spinning' : ''} />
                    </button>
                </div>
            </div>

            {/* Filters */}
            <div className="logs-filters">
                <form className="logs-search-form" onSubmit={handleSearch}>
                    <div className="logs-search-wrap">
                        <FiSearch className="logs-search-icon" />
                        <input
                            className="logs-search-input"
                            placeholder="Tìm tên, CitizenID..."
                            value={searchInput}
                            onChange={e => setSearchInput(e.target.value)}
                        />
                    </div>
                    <button type="submit" className="logs-search-btn">Tìm</button>
                    {search && (
                        <button type="button" className="logs-clear-btn"
                            onClick={() => { setSearchInput(''); setSearch(''); }}>
                            Xóa
                        </button>
                    )}
                </form>

                <div className="logs-date-filters">
                    <input type="datetime-local" className="logs-date-input"
                        value={dates.startDate} onChange={e => setDates(p => ({ ...p, startDate: e.target.value }))} />
                    <span className="logs-date-sep">→</span>
                    <input type="datetime-local" className="logs-date-input"
                        value={dates.endDate}   onChange={e => setDates(p => ({ ...p, endDate:   e.target.value }))} />
                    <div className="logs-date-presets">
                        <button type="button" className="logs-preset-btn" onClick={handleToday}>Hôm nay</button>
                        <button type="button" className="logs-preset-btn" onClick={handle7days}>7 ngày</button>
                        <button type="button" className="logs-preset-btn"
                            onClick={() => setDates({ startDate: '', endDate: '' })}>Tất cả</button>
                    </div>
                </div>
            </div>

            {/* Mod Shift dashboard analytics — chỉ render cho tab Lịch Trực Mod */}
            {groupKey === 'rvn_modshift_logs' && <ModShiftSummary />}

            <FiveMLogTable
                logs={logs}
                loading={loading}
                pagination={pagination}
                onPageChange={fetchLogs}
                table={groupKey}
            />
        </div>
    );
};

export default GroupLogs;
